import { Component } from '@angular/core';
import { ApiUrlHelper } from '../../../../common/api-url-helper';
import { CommonService } from '../../../../services/common/common.service';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { StateService } from '../../../../services/common/claim-year.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router } from '@angular/router';
import { SoftwareByProject, commonLabelConstants } from '../../../../constants/label-constants';
import { environment } from '../../../../../environments/environment';
import { CustomCurrencyPipe } from '../../../../common/pipes/custom-currency.pipe';
import { SharedService } from '../../../../services/shared-service/shared.service';
import { Subject, takeUntil } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { ValidationService } from '../../../../services/common/validation-service';

@Component({
  selector: 'app-software-project-form',
  templateUrl: './software-project-form.component.html',
  styleUrl: './software-project-form.component.scss'
})

export class SoftwareProjectFormComponent {

  env: any = environment;
  projectList: any = [];
  companyId: any = 0
  claimYearId: any = 0;
  softwareRNDForm!: FormGroup;
  detail: any = []
  isLoading: boolean = false;
  labelConstants = SoftwareByProject;
  commonLabelConstants = commonLabelConstants;
  onDestroy$: Subject<void> = new Subject();
  isLocked: boolean = false;

  constructor(
    private readonly api: ApiUrlHelper,
    private readonly commonService: CommonService,
    private readonly sharedDataService: StateService,
    private readonly spinner: NgxSpinnerService,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly sharedService: SharedService,
    private readonly currencyPipe: CustomCurrencyPipe,
    private readonly validationService:ValidationService) {
  }

  get software(): FormArray {
    return this.softwareRNDForm.get('software') as FormArray
  }

  ngOnInit() {
    this.softwareRNDForm = this.fb.group({
      software: this.fb.array([])
    });
    this.sharedDataService.companyId$.subscribe((companyId) => {
      this.companyId = companyId;
    });
    this.sharedDataService.yearId$.pipe(takeUntil(this.onDestroy$)).subscribe((yearId) => {
      this.claimYearId = yearId ?? null;

      if (this.companyId && this.claimYearId) {
        this.resetForm();
        this.getProjectListCompanyWise(this.companyId, this.claimYearId)
      }
    });
  }

  resetForm() {
    this.softwareRNDForm = this.fb.group({
      software: this.fb.array([])
    });
  }

  getProjectListCompanyWise(companyId: any, claimYearId: any) {
    this.spinner.show();
    let api = this.api.apiUrl.Project.GetProjectListWithOutPagination.replace('{companyId}', companyId).replace('{claimYearId}', claimYearId);
    this.commonService.doGet(api).pipe().subscribe({
      next: (response) => {
        if (response.success) {
          this.projectList = response.data;
        }
        else {
          this.projectList = [];
        }
        this.getSoftwareDetail();
        this.spinner.hide();
      },
      error: (error: any) => {
        this.spinner.hide();
        console.log(error);
      }
    });
  }

  createSoftware(): FormGroup {
    return this.fb.group({
      softwareId: [null],
      softwareResourceId: [null],
      descriptionOfLicence: [{ value: '', disabled: true }],
      projects: this.fb.array([]),
      totalGross: [{ value: '', disabled: true }],
      totalEligibilityPer: [{ value: 0, disabled: true }],
    });
  }

  createSoftwareProjectDetail() {
    return this.fb.group({
      softwareEligibilityId: [null],
      softwareResourceId: [null],
      projectId: [''],
      rndEligibility: [''],
    });
  }

  getItemAtIndex(index: number): FormGroup {
    return this.software.at(index) as FormGroup;
  }

  getProjectDetails(index: any) {
    return this.getItemAtIndex(index).get('projects') as FormArray;
  }

  getProjectTotal(projectIndex: number): number {
    let total = 0;
    this.software.controls.forEach(softwareControl => {
      const projectsArray = softwareControl.get('projects') as FormArray;
      const rndEligibilityValue = projectsArray.at(projectIndex)?.get('rndEligibility')?.value;
      if (rndEligibilityValue) {
        total += parseFloat(rndEligibilityValue);
      }
    });
    return total;
  }

  getGrandTotal(): number {
    return this.software.controls.reduce((grandTotal, softwareControl, index) => {
      return grandTotal + this.getRndEligibilitySum(index);
    }, 0);
  }

  getSoftwareDetail() {
    this.isLoading = true;
    let obj = {
      companyId: this.companyId,
      claimYearId: this.claimYearId
    }
    let api = this.api.apiUrl.SoftwareResource.GetSoftwareResource;
    this.commonService.doPost(api, obj).pipe().subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success) {
          this.detail = response.data;
           this.isLocked = ( this.detail[0]?.isLocked ?? false);
          this.detail.forEach((item: any) => {
            const newSoftware = this.createSoftware();
            newSoftware.patchValue({
              softwareId: item.softwareId,
              descriptionOfLicence: item.descriptionOfLicence,
              softwareResourceId: item.softwareResourceId,
              totalGross: item.cost,
              totalEligibilityPer: item.totalEligibilityAmount,
            });
            const softwareDetailsArray = newSoftware.get('projects') as FormArray;
            this.projectList.forEach((itemDetail: any) => {
              const newProjectDetail = this.createSoftwareProjectDetail();

              const projectData = item.projects.find(
                (projectDetail: any) =>
                  projectDetail.projectId == itemDetail.projectId
              );

              const rndEligibilityValue =
                projectData?.rndEligibility
                  ? projectData.rndEligibility
                  : '0';
              newProjectDetail.patchValue({
                softwareEligibilityId: projectData
                  ? projectData.staffCostResourceEligibilityId
                  : 0,
                softwareResourceId: projectData
                  ? projectData.staffCostResourceId
                  : 0,
                projectId: itemDetail.projectId,
                rndEligibility: rndEligibilityValue,
              });
              newProjectDetail.disable();

              softwareDetailsArray.push(newProjectDetail);
            });
            this.software.push(newSoftware);
          })
        }
      },
      error: (error: any) => {
        this.isLoading = false;
        console.log(error);
        this.spinner.hide();
      }
    });
  }

  getRndEligibilitySum(softwareIndex: number): number {
    const projectDetails = this.getProjectDetails(softwareIndex).controls;
    let sum = 0;
    projectDetails.forEach((project: any) => {
      const rndEligibility = project.get('rndEligibility').value ? project.get('rndEligibility').value : 0;
      sum += rndEligibility ? parseFloat(rndEligibility) : 0;
    });
    return sum;
  }

  getCostTotal(): number {
    return this.software.controls.reduce((totalCost, contractor) => {
      const cost = contractor.get('totalGross')?.value || 0;
      return totalCost + Number(cost);
    }, 0);
  }

  getTotalProduct(projectIndex: number): string | null {
    let totalProduct = 0;

    for (let i = 0; i < this.software.length; i++) {
      const employee = this.getItemAtIndex(i);
      const projectDetails = this.getProjectDetails(i);

      if (projectIndex < projectDetails.length) {
        const totalGrossStr = employee.get('totalGross')?.value;
        const totalGross = parseFloat(totalGrossStr) || 0;

        const rndEligibilityValueStr = projectDetails.at(projectIndex).get('rndEligibility')?.value;
        const rndEligibility = (parseFloat(rndEligibilityValueStr) || 0) / 100;

        totalProduct += totalGross * rndEligibility;
      } else {
        console.warn(`Project index ${projectIndex} is out of bounds for employee ${i}.`);
      }
    }
    return this.currencyPipe.transform(totalProduct === 0 ? 0 : Number(totalProduct.toFixed(2)), environment.CurrencyFormat);
  }

  getTotalQualifyingCosts(): number {
    let total = 0;
    const employeeArray = this.softwareRNDForm.get('software') as FormArray;

    employeeArray.controls.forEach((employee) => {
      total += employee.get('totalEligibilityPer')?.value || 0;
    });
    return total;
  }

  goBack(): void {
    this.router.navigateByUrl('/dashboard');
  }
  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  exportList(): void {
    const isValid = this.validationService.validateCompanyAndYear(
      this.companyId,
      this.claimYearId
    );
    if (!isValid) {
      return;
    }
    const companyIdStr = this.companyId !== null ? String(this.companyId) : '';
    const yearIdStr = this.claimYearId !== null ? String(this.claimYearId) : '';
    let obj = {
      CompanyId: companyIdStr,
      ClaimYearId: yearIdStr,
    };
    let api = this.api.apiUrl.SoftwareResource.ExportSoftwareCostCategory;
    this.commonService.doDownloadPost(api, obj).subscribe(
      (response: HttpResponse<Blob>) => {
        const blob = response.body as Blob;
        const link = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.download = "Software - Qualifying Expenditure by Cost Category.xlsx";

        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      },
      (error: any) => {
        console.log(error);
      }
    );
  }
}
